import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, ErroApi, guardarContexto } from '../api';
import { useAuth } from '../auth';
import { Logo } from '../componentes/Logo';
import { Erro } from '../componentes/ui';
import { contextosDe } from '../contexto';

interface Estado {
  celular: string;
  celularFormatado: string;
  validadeMinutos?: number;
  codigoDeBanca?: string;
}

const CASAS = 6;

export function Codigo() {
  const nav = useNavigate();
  const loc = useLocation();
  const { entrar } = useAuth();
  const estado = loc.state as Estado | null;

  const [digitos, setDigitos] = useState<string[]>(Array(CASAS).fill(''));
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [espera, setEspera] = useState(0);
  const [reenviado, setReenviado] = useState(false);
  const [codigoDeBanca, setCodigoDeBanca] = useState(estado?.codigoDeBanca);
  const campos = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => { campos.current[0]?.focus(); }, []);
  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  if (!estado) return <Navigate to="/entrar" replace />;

  async function confirmar(codigo: string) {
    setErro('');
    setEnviando(true);
    try {
      const r = await api.confirmarCodigo(estado!.celular, codigo);
      entrar(r.token, r.usuario);
      // Mais de uma conta no mesmo celular (admin, contador, cliente):
      // pergunta com qual entrar. Só uma: nem precisa perguntar.
      const opcoes = contextosDe(r.usuario);
      if (opcoes.length <= 1) {
        guardarContexto(opcoes[0] ?? null);
        nav('/painel', { replace: true });
      } else {
        nav('/escolher', { replace: true });
      }
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui confirmar o código.');
      setDigitos(Array(CASAS).fill(''));
      campos.current[0]?.focus();
    } finally {
      setEnviando(false);
    }
  }

  function escrever(i: number, valor: string) {
    const limpo = valor.replace(/\D/g, '');
    if (!limpo) {
      const proximo = [...digitos];
      proximo[i] = '';
      setDigitos(proximo);
      return;
    }
    // Colar o código inteiro numa casa só espalha pelas seguintes — é o
    // que acontece quando a pessoa copia da conversa do WhatsApp.
    const proximo = [...digitos];
    for (let k = 0; k < limpo.length && i + k < CASAS; k += 1) proximo[i + k] = limpo[k];
    setDigitos(proximo);
    const ultimo = Math.min(i + limpo.length, CASAS - 1);
    campos.current[ultimo]?.focus();
    const completo = proximo.join('');
    if (completo.length === CASAS && !proximo.includes('')) void confirmar(completo);
  }

  function tecla(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digitos[i] && i > 0) campos.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) campos.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < CASAS - 1) campos.current[i + 1]?.focus();
  }

  async function reenviar() {
    setErro('');
    try {
      const r = await api.solicitarCodigo(estado!.celular);
      setCodigoDeBanca(r.codigoDeBanca);
      setReenviado(true);
      setEspera(60);
    } catch (e) {
      if (e instanceof ErroApi) {
        setErro(e.message);
        if (e.esperarSegundos) setEspera(e.esperarSegundos);
      }
    }
  }

  return (
    <div className="auth-split">
      <div className="auth-panel">
        <Logo tamanho={36} tamanhoFonte={24} tom="sobreAcento" />
        <div style={{ maxWidth: 460 }}>
          <h1 style={{ fontSize: 40, lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: 16 }}>
            Um código, e você entra.
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.65 }}>
            Ele vale só para esta entrada e expira em poucos minutos. Se não foi você quem pediu,
            é só ignorar a mensagem — ninguém entra sem o código.
          </p>
        </div>
        <div className="copyright">© 2026 Conti9 · um produto <strong>Grupo Atendi9</strong></div>
      </div>

      <div className="auth-form-wrap">
        <div className="auth-form fade-up">
          <h2 style={{ fontSize: 29, letterSpacing: '-0.03em', marginBottom: 8 }}>Digite o código</h2>
          <p className="muted" style={{ fontSize: 15, marginBottom: 24 }}>
            Enviado para <strong>{estado.celularFormatado}</strong>
            {estado.validadeMinutos ? ` · vale por ${estado.validadeMinutos} minutos` : ''}.
          </p>

          {/* Em MENSAGERIA_PROVEDOR=log nada sai no WhatsApp. Mostrar o
              código aqui é o que permite usar o portal antes de a instância
              do Evolution existir — e o aviso deixa claro que é banca. */}
          {codigoDeBanca && (
            <div className="aviso-banca">
              <strong>Modo de banca:</strong> a mensagem não foi para o WhatsApp.
              O código é <code>{codigoDeBanca}</code>.
            </div>
          )}

          <Erro>{erro}</Erro>

          <div className="otp-row">
            {digitos.map((d, i) => (
              <input
                key={i}
                ref={(el) => { campos.current[i] = el; }}
                className="otp-box"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={CASAS}
                value={d}
                disabled={enviando}
                onChange={(e) => escrever(i, e.target.value)}
                onKeyDown={(e) => tecla(i, e)}
              />
            ))}
          </div>

          <button
            className="btn btn-primary"
            style={{ marginTop: 22 }}
            disabled={enviando || digitos.includes('')}
            onClick={() => confirmar(digitos.join(''))}
          >
            {enviando ? <span className="spinner" /> : 'Entrar'}
          </button>

          <div className="auth-rodape">
            {espera > 0
              ? <span>Reenviar em {espera}s</span>
              : (
                <button className="link-botao" onClick={reenviar}>
                  {reenviado ? 'Enviar outro código' : 'Não recebi o código'}
                </button>
              )}
          </div>

          <div className="auth-rodape">
            <button className="link-botao" onClick={() => nav('/entrar', { replace: true })}>
              Usar outro número
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
